import React from 'react';
import { Box, Text, VStack, UnorderedList, ListItem, Link } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { getCustomFieldValueType } from '../../../utils/tableUtils';
import ReactMarkdown from 'react-markdown';

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const ShimmerSkeleton = () => (
  <VStack align="stretch" spacing={1.5} w="100%" py={0.5}>
    {[75, 55].map((pct, i) => (
      <Box
        key={i}
        h="10px"
        w={`${pct}%`}
        borderRadius="5px"
        animation={`${shimmer} 1.6s ease-in-out infinite`}
        background="linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.10) 40%, rgba(255,255,255,0.04) 100%)"
        backgroundSize="1200px 100%"
      />
    ))}
  </VStack>
);

/**
 * Component to render custom field values with rich formatting.
 * Handles arrays, objects, and arrays of objects with proper styling.
 *
 * Display formats:
 * - Arrays: Bullet point list
 * - Objects: Key (bold): Value
 * - Arrays of objects: Bullet list with key-value pairs for each item
 * - Primitives: Simple text display
 *
 * @param {Object} props
 * @param {*} props.value - The value to display
 * @param {string} props.size - Size variant ('sm', 'md', 'lg') - default 'sm'
 * @param {boolean} props.isGenerating - Show skeleton shimmer instead of dash when value is empty
 */
const URL_RE = /^https?:\/\/\S+$/i;
const MARKDOWN_LINK_RE = /\[.*?\]\(.*?\)/;

const renderInlineValue = (val, fontSize, textColor) => {
  if (val === null || val === undefined) return '—';
  const str = String(val);
  if (URL_RE.test(str.trim())) {
    return (
      <Link
        href={str.trim()}
        color="rgba(96,165,250,0.9)"
        textDecoration="underline"
        textDecorationColor="rgba(96,165,250,0.35)"
        _hover={{ color: '#93c5fd', textDecorationColor: 'rgba(147,197,253,0.6)' }}
        isExternal
        onClick={(e) => e.stopPropagation()}
        fontSize={fontSize}
      >
        {str.trim()}
      </Link>
    );
  }
  if (MARKDOWN_LINK_RE.test(str)) {
    return (
      <Box as="span" fontSize={fontSize} color={textColor}>
        <ReactMarkdown
          components={{
            p: ({ node, ...props }) => <Text as="span" {...props} />,
            a: ({ node, ...props }) => (
              <Link
                {...props}
                color="rgba(96,165,250,0.9)"
                textDecoration="underline"
                textDecorationColor="rgba(96,165,250,0.35)"
                _hover={{ color: '#93c5fd', textDecorationColor: 'rgba(147,197,253,0.6)' }}
                isExternal
                onClick={(e) => e.stopPropagation()}
              />
            ),
          }}
        >
          {str}
        </ReactMarkdown>
      </Box>
    );
  }
  return str;
};

const CustomFieldDisplay = ({ value, size = 'sm', isGenerating = false }) => {
  const valueType = getCustomFieldValueType(value);

  const fontSize = size === 'lg' ? 'md' : 'sm';

  const emptyColor = 'rgba(255,255,255,0.2)';
  const textColor = '#a3a3a3';
  const keyColor = '#fafafa';

  switch (valueType) {
    case 'null':
      return isGenerating ? (
        <ShimmerSkeleton />
      ) : (
        <Text fontSize={fontSize} color={emptyColor}>
          —
        </Text>
      );

    case 'boolean':
      return (
        <Text fontSize={fontSize} color={textColor}>
          {value ? 'true' : 'false'}
        </Text>
      );

    case 'number':
      return (
        <Text fontSize={fontSize} color={textColor}>
          {String(value)}
        </Text>
      );

    case 'string': {
      if (value.trim() === '') {
        return isGenerating ? (
          <ShimmerSkeleton />
        ) : (
          <Text fontSize={fontSize} color={emptyColor}>
            —
          </Text>
        );
      }
      const rendered = renderInlineValue(value, fontSize, textColor);
      if (typeof rendered !== 'string') {
        return rendered;
      }
      return (
        <Text fontSize={fontSize} color={textColor} whiteSpace="pre-wrap" wordBreak="break-word">
          {value}
        </Text>
      );
    }

    case 'array':
      if (value.length === 0) {
        return isGenerating ? (
          <ShimmerSkeleton />
        ) : (
          <Text fontSize={fontSize} color={emptyColor}>
            —
          </Text>
        );
      }
      return (
        <UnorderedList spacing={1} ml={4} mt={1}>
          {value.map((item, idx) => (
            <ListItem key={idx} fontSize={fontSize} color={textColor}>
              {renderInlineValue(item, fontSize, textColor)}
            </ListItem>
          ))}
        </UnorderedList>
      );

    case 'array-of-objects':
      if (value.length === 0) {
        return isGenerating ? (
          <ShimmerSkeleton />
        ) : (
          <Text fontSize={fontSize} color={emptyColor}>
            —
          </Text>
        );
      }
      return (
        <UnorderedList spacing={2} ml={4} mt={1}>
          {value.map((obj, idx) => (
            <ListItem key={idx} fontSize={fontSize} color={textColor}>
              <VStack align="start" spacing={0.5}>
                {Object.entries(obj).map(([key, val]) => (
                  <Text key={key} fontSize={fontSize}>
                    <Text as="span" fontWeight="600" color={keyColor}>
                      {key}:
                    </Text>{' '}
                    <Text as="span" color={textColor}>
                      {renderInlineValue(val, fontSize, textColor)}
                    </Text>
                  </Text>
                ))}
              </VStack>
            </ListItem>
          ))}
        </UnorderedList>
      );

    case 'object':
      if (Object.keys(value).length === 0) {
        return isGenerating ? (
          <ShimmerSkeleton />
        ) : (
          <Text fontSize={fontSize} color={emptyColor}>
            —
          </Text>
        );
      }
      return (
        <VStack align="start" spacing={1} mt={1}>
          {Object.entries(value).map(([key, val]) => (
            <Text key={key} fontSize={fontSize}>
              <Text as="span" fontWeight="600" color={keyColor}>
                {key}:
              </Text>{' '}
              <Text as="span" color={textColor}>
                {renderInlineValue(val, fontSize, textColor)}
              </Text>
            </Text>
          ))}
        </VStack>
      );

    default:
      return (
        <Text fontSize={fontSize} color={textColor} whiteSpace="pre-wrap" wordBreak="break-word">
          {String(value)}
        </Text>
      );
  }
};

export default CustomFieldDisplay;
